import type { JSTAppModelRecord } from "../../../tare-spec/NativeJSTAppModel";
import { getJSTAppModel, getJSTAppModelSync } from "../../../tare/model/JSTAppModel";
import { normalizeEnvironment } from "../../environment";
import { callPromiseSafely } from "../../util/callPromiseSafely";
import type { TareAppModel } from "../types/TareBridge";
import { getTareBridge } from "./getTareBridge";
import { isTareCodegenActive, isTareCodegenActiveSync } from "./tareCodegenMigration";

/**
 * Normalizes the buildEnvironment field from the raw native bridge value.
 * Android sends uppercase ('PROD', 'QA'), iOS sends lowercase ('prod', 'qa').
 */
function normalizeAppModel(appModel: TareAppModel | null): TareAppModel | null {
  if (!appModel) {
    return null;
  }
  return {
    ...appModel,
    buildEnvironment: normalizeEnvironment(appModel.buildEnvironment),
  };
}

/**
 * Widens a spec record to the bridge-agnostic `TareAppModel` callers expect. The spec's
 * `buildEnvironment` is already uniform across platforms, but is still normalized so both
 * branches of `getAppModel` return the same `CanonicalEnvironment` casing.
 */
function mapCodegenAppModel(record: JSTAppModelRecord): TareAppModel {
  return {
    appVersion: record.appVersion,
    buildConfig: record.buildConfig,
    buildEnvironment: normalizeEnvironment(record.buildEnvironment),
    locale: {
      languageCode: record.locale.languageCode,
      countryCode: record.locale.countryCode,
      indeedLanguageCode: record.locale.indeedLanguageCode,
      indeedCountryCode: record.locale.indeedCountryCode,
    },
  };
}

/**
 * Load the app model from the native module.
 */
export async function getAppModel(): Promise<TareAppModel | null> {
  if (await isTareCodegenActive()) {
    const record = await getJSTAppModel();
    return record ? mapCodegenAppModel(record) : null;
  }
  const result = await callPromiseSafely(getTareBridge()?.getAppModel);
  return normalizeAppModel(result);
}

export function getAppModelSync(): TareAppModel | null {
  if (isTareCodegenActiveSync()) {
    const record = getJSTAppModelSync();
    return record ? mapCodegenAppModel(record) : null;
  }
  const result = getTareBridge()?.getAppModelSync?.() ?? null;
  return normalizeAppModel(result);
}
